import { capitalize, compact, flattenDeep, pick, range, round } from 'lodash';
import { DateTime } from 'luxon';
import * as z from 'zod';

import {
  convertStringToDate,
  CreateLoanRow,
  ForeignCountryCode,
  isConstructionLoan,
  isHelocLoan,
  PaymentFrequency,
  StrictAddress,
} from '@willow/types-iso';

import { formatDate } from '../../../utils';
import { add, addPrecision } from '../../../utils/math';
import { Sanitizer } from '../../shared/sanitizeRequest';
import { NUM_BOARDING_INSURANCES, NUM_BOARDING_PROPERTIES, NUM_BOARDING_TAX_OTHERS } from './createFieldConsts';
import {
  validateAppraisalFields,
  validateOriginalAppraisalDateIsBeforeCorrespondingAppraisalDate,
} from './crossFieldParsers/validateAppraisalFields';
import { validateArmFields } from './crossFieldParsers/validateArmFields';
import { validateEmailAddresses } from './crossFieldParsers/validateEmailAddresses';
import { validateTerminationFeeFields } from './crossFieldParsers/validateFeeFields';
import { validateInvestorFields } from './crossFieldParsers/validateInvestorFields';
import { validateLoanTermMatch } from './crossFieldParsers/validateLoanTermMatch';
import { validatePaymentFrequencyFields } from './crossFieldParsers/validatePaymentFrequencyFields';

export type CrossFieldParserContext = 'api' | 'csv';

const CACHE = new Map<string, any>();

// --- REFINEMENTS --- //

// UTILITY FOR PARSING JUST THE FIELDS WE NEED
// Otherwise, the refinements may fail for reasons unrelated to user-facing the error message
// If this function returns false, the refinement must exit early.
export const getParsedFields = <T, K extends keyof CreateLoanRow>(data: T, fields: K[]) => {
  const zodPickArgs: { [_ in K]?: boolean } = {};
  for (const field of fields) {
    zodPickArgs[field] = true;
  }

  const partial = pick(data, fields);

  const sanitized = Sanitizer.safeParse(partial);

  if (!sanitized.success) {
    return false;
  }

  const schemaCacheKey = fields.sort().join('-');
  let parser = CACHE.get(schemaCacheKey);
  if (!parser) {
    parser = CreateLoanRow.pick(zodPickArgs);
    CACHE.set(schemaCacheKey, parser);
  }

  const parsed = parser.safeParse(sanitized.data);

  if (!parsed.success) {
    return false;
  }

  return parsed.data;
};

const validateIdFields = (data: CreateLoanRow, ctx: z.RefinementCtx<CreateLoanRow>) => {
  const parsed = getParsedFields(data, [
    'loanId',
    'poolId',
    'previousServicerId',
    'capitalPartnerId',
    'fullId',
    'otherId1',
    'otherId2',
    'otherId3',
    'ownerId',
    'investorLoanId',
  ]);
  if (!parsed) {
    return;
  }

  for (const [key] of Object.entries(parsed)) {
    // Note: excel is sometimes changing id fields to scientific format (ie 20+15243)
    if (parsed[key] && parsed[key].indexOf('+') >= 0) {
      ctx.addIssue({
        code: 'custom',
        message: 'ID contains invalid "+" character',
        path: [key],
      });
    }
  }
};

const dateFieldsAndLoanTermMatch = (data: CreateLoanRow, ctx: z.RefinementCtx<CreateLoanRow>) => {
  const parsed = getParsedFields(data, [
    'firstPaymentDate',
    'loanMaturityDate',
    'loanTermMonths',
    'loanTermYears',
    'constructionLoanType',
    'paymentFrequency',
  ]);

  // Don't continue if any of the fields failed validation
  // Those errors bubble up to the client so they can fix
  if (!parsed) {
    return;
  }

  if (!parsed.loanTermMonths && !parsed.loanTermYears) {
    ctx.addIssue({
      code: 'custom',
      message: 'Either `loanTermMonths` or `loanTermYears` must be provided.',
    });
    return;
  }

  if (parsed.constructionLoanType === 'constructionToPermanent') {
    // Do not throw error if loan is constructionToPermanent since it will change later anyway
    return;
  }

  const termInMonths = parsed.loanTermMonths ? Number(parsed.loanTermMonths) : Number(parsed.loanTermYears) * 12;

  const paymentFrequency = parsed.paymentFrequency != null ? parseInt(parsed.paymentFrequency, 10) : 12;

  const isValidLoanTerm = validateLoanTermMatch({
    loanTermMonths: termInMonths,
    firstPaymentDate: convertStringToDate(parsed.firstPaymentDate),
    maturityDate: convertStringToDate(parsed.loanMaturityDate),
    paymentFrequency: paymentFrequency as PaymentFrequency,
  });

  if (!isValidLoanTerm) {
    ctx.addIssue({
      code: 'custom',
      message: 'Loan maturity date does not match the first payment date + loan term.',
      path: ['loanMaturityDate'],
    });
  }
};

const hasPaymentFields = (data: CreateLoanRow, ctx: z.RefinementCtx<CreateLoanRow>) => {
  const parsed = getParsedFields(data, [
    'loanType',
    'productType',
    'constructionLoanType',
    'firstPaymentPrincipalAndInterest',
    'firstPaymentPrincipal',
    'firstPaymentInterest',
    'drawsEnabled',
    'manualStatementGeneration',
  ]);

  if (!parsed) {
    return;
  }

  // We dont care about this validation for construction loans or draw enabled loans
  if (
    isConstructionLoan(parsed.constructionLoanType) ||
    parsed.productType === 'businessPurpose' ||
    parsed.productType === 'bridge' ||
    parsed.drawsEnabled ||
    parsed.manualStatementGeneration
  ) {
    return;
  }

  if (parsed.firstPaymentPrincipalAndInterest == null) {
    if (parsed.firstPaymentPrincipal == null && parsed.firstPaymentInterest == null) {
      ctx.addIssue({
        code: 'custom',
        message:
          'Missing required field for payment. Must include either `firstPaymentPrincipalAndInterest` or `firstPaymentPrincipal` and `firstPaymentInterest`',
      });
    } else if (parsed.firstPaymentPrincipal == null) {
      ctx.addIssue({
        code: 'custom',
        message: 'Missing required field `firstPaymentPrincipal`',
      });
    } else if (parsed.firstPaymentInterest == null) {
      ctx.addIssue({
        code: 'custom',
        message: 'Missing required field `firstPaymentInterest`',
      });
    }
  }
};

const checkFirstCollectedPaymentDate = (data: CreateLoanRow, ctx: z.RefinementCtx<CreateLoanRow>) => {
  const parsed = getParsedFields(data, ['firstCollectedPaymentDate', 'firstPaymentDate']);

  // Only run this validator if first collected payment date exists
  if (!parsed || !parsed.firstCollectedPaymentDate) {
    return;
  }

  const firstCollectedPaymentDate = convertStringToDate(parsed.firstCollectedPaymentDate);
  const firstPaymentDate = convertStringToDate(parsed.firstPaymentDate);

  if (firstCollectedPaymentDate < firstPaymentDate) {
    ctx.addIssue({
      code: 'custom',
      message: '`firstCollectedPaymentDate` cannot be before `firstPaymentDate`',
      path: ['firstCollectedPaymentDate'],
    });
  }
};

const hasDrawFields = (data: CreateLoanRow, ctx: z.RefinementCtx<CreateLoanRow>) => {
  const parsed = getParsedFields(data, [
    'drawsEnabled',
    'maxLineOfCredit',
    'drawTerm',
    'drawExpirationDate',
    'repayBeginDate',
    'interestOnlyFlag',
    'draw1Date',
    'draw1Amount',
    'draw2Date',
    'draw2Amount',
    'draw3Date',
    'draw3Amount',
  ]);

  if (!parsed) {
    return;
  }

  if (parsed.drawsEnabled && !parsed.maxLineOfCredit) {
    ctx.addIssue({
      code: 'custom',
      message: 'Missing required field `maxLineOfCredit` when `drawsEnabled` = true',
      path: ['drawsEnabled'],
    });
  }

  if (parsed.drawsEnabled && !parsed.interestOnlyFlag) {
    ctx.addIssue({
      code: 'custom',
      message: 'Missing required field `interestOnlyFlag` when `drawsEnabled` = true',
      path: ['drawsEnabled'],
    });
  }

  if (!parsed.drawsEnabled) {
    if (parsed.drawTerm) {
      ctx.addIssue({
        code: 'custom',
        message: 'Set `drawsEnabled` = true when setting `drawTerm`',
        path: ['drawTerm'],
      });
    }

    if (parsed.drawExpirationDate) {
      ctx.addIssue({
        code: 'custom',
        message: 'Set `drawsEnabled` = true when setting `drawExpirationDate`',
        path: ['drawExpirationDate'],
      });
    }

    if (parsed.repayBeginDate) {
      ctx.addIssue({
        code: 'custom',
        message: 'Set `drawsEnabled` = true when setting `repayBeginDate`',
        path: ['repayBeginDate'],
      });
    }
  }

  for (const drawNum of range(1, 4)) {
    const drawDate = parsed[`draw${drawNum}Date`];
    const drawAmount = parsed[`draw${drawNum}Amount`];

    if (!parsed.drawsEnabled && (drawDate || drawAmount)) {
      ctx.addIssue({
        code: 'custom',
        message: `Set \`drawsEnabled\` = true when setting \`draw${drawNum}Date\` or \`draw${drawNum}Amount\``,
      });
    }

    if ((drawDate && drawAmount == null) || (drawDate == null && drawAmount != null)) {
      ctx.addIssue({
        code: 'custom',
        message: `Both \`draw${drawNum}Date\` and \`draw${drawNum}Amount\` must have values if one is set`,
      });
    }
  }
};

const validateInterestOnlyFields = (data: CreateLoanRow, ctx: z.RefinementCtx<CreateLoanRow>) => {
  const parsed = getParsedFields(data, [
    'interestOnlyFlag',
    'interestOnlyTermMonths',
    'interestOnlyTermYears',
    'interestOnlyEndDate',
    'firstPaymentDate',
  ]);

  if (!parsed) {
    return;
  }

  if (
    parsed.interestOnlyFlag &&
    !(parsed.interestOnlyTermMonths || parsed.interestOnlyTermYears || parsed.interestOnlyEndDate)
  ) {
    ctx.addIssue({
      code: 'custom',
      message:
        'Missing required field `interestOnlyTermMonths` or `interestOnlyTermYears` or `interestOnlyEndDate` when `interestOnlyFlag` = true',
      path: ['interestOnlyFlag'],
    });
  }

  if (parsed.interestOnlyEndDate && (parsed.interestOnlyTermMonths || parsed.interestOnlyTermYears)) {
    // Check that the fields do not conflict
    const termMonths = parsed.interestOnlyTermMonths || parsed.interestOnlyTermYears * 12;
    const expectedEndDate = DateTime.fromJSDate(new Date(parsed.firstPaymentDate)).plus({ months: termMonths });
    if (!expectedEndDate.equals(DateTime.fromJSDate(new Date(parsed.interestOnlyEndDate)))) {
      ctx.addIssue({
        code: 'custom',
        message:
          'interestOnlyEndDate does not match the interest-only term set in `interestOnlyTermMonths` or `interestOnlyTermYears`',
        path: ['interestOnlyEndDate'],
      });
    }
  }
};

const hasEscrowFields = (data: CreateLoanRow, ctx: z.RefinementCtx<CreateLoanRow>) => {
  const parsed = getParsedFields(data, ['isEscrowed', 'monthlyPaymentEscrow', 'miAmount']);

  if (!parsed) {
    return;
  }

  if (parsed.isEscrowed && !parsed.monthlyPaymentEscrow && parsed.miAmount == null) {
    ctx.addIssue({
      code: 'custom',
      message: 'Missing required field `miAmount` or `monthlyPaymentEscrow` (deprecated) when `isEscrowed` = true',
      path: ['isEscrowed'],
    });
  }
};

const hasActiveLoanFields = (data: CreateLoanRow, ctx: z.RefinementCtx<CreateLoanRow>) => {
  const parsed = getParsedFields(data, [
    'constructionLoanType',
    'currentOutstandingAmount',
    'firstCollectedPaymentDate',
    'initialInterestBalance',
    'firstPaymentDate',
    'productType',
    'minMonthlyPayment',
  ]);

  if (!parsed) {
    return;
  }

  const REQUIRED_ACTIVE_LOAN_FIELDS: (keyof CreateLoanRow)[] = compact([
    'currentOutstandingAmount',
    'firstCollectedPaymentDate',
  ]);

  // If the first payment date is in the past, check if firstCollectedPaymentDate is present.
  const firstPaymentDateIsInPast =
    DateTime.fromJSDate(new Date(parsed.firstPaymentDate)).startOf('day') < DateTime.now().startOf('day');

  if (firstPaymentDateIsInPast && !parsed.firstCollectedPaymentDate) {
    ctx.addIssue({
      code: 'custom',
      message:
        'Active loan is missing required field "firstCollectedPaymentDate". (Active loan is denoted by "firstPaymentDate" in the past)',
    });

    return;
  }

  // If "firstCollectedPaymentDate" is same as "firstPaymentDate", we don't need the other active loan fields
  // because nothing has happened to the loan yet.
  const hasActiveFirstCollectedPaymentDate =
    parsed.firstCollectedPaymentDate &&
    DateTime.fromJSDate(new Date(parsed.firstCollectedPaymentDate)).startOf('day') >
      DateTime.fromJSDate(new Date(parsed.firstPaymentDate)).startOf('day');

  const isActive = parsed.currentOutstandingAmount || hasActiveFirstCollectedPaymentDate;

  if (!isActive) {
    return;
  }

  const missingFields: (keyof CreateLoanRow)[] = [];

  for (const key of REQUIRED_ACTIVE_LOAN_FIELDS) {
    // @ts-ignore
    if (parsed[key] == null) {
      missingFields.push(key);
    }
  }

  if (missingFields.length) {
    const missingFieldsString = missingFields.map((key) => `\`${String(key)}\``).join(', ');
    ctx.addIssue({
      code: 'custom',
      message: `Active loan is missing required ${
        missingFields.length > 1 ? 'fields' : 'field'
      } ${missingFieldsString}. (Active loan is denoted by presence of \`currentOutstandingAmount\` or \`firstCollectedPaymentDate\`)`,
    });
  }
};

const REQUIRED_BORROWER_MAILING_ADDRESS_FIELDS_US: (keyof CreateLoanRow)[] = [
  'primaryBorrowerMailingAddressLine1',
  'primaryBorrowerMailingAddressLocality',
  'primaryBorrowerMailingAddressRegion',
  'primaryBorrowerMailingAddressPostcode',
];

const REQUIRED_BORROWER_MAILING_ADDRESS_FIELDS_FOREIGN: (keyof CreateLoanRow)[] = [
  'primaryBorrowerMailingAddressLine1',
  'primaryBorrowerMailingAddressLocality',
];

const hasBorrowerMailingAddressFields = (data: CreateLoanRow, ctx: z.RefinementCtx<CreateLoanRow>) => {
  const requiredFields = ForeignCountryCode.safeParse(data.primaryBorrowerMailingAddressCountry).success
    ? REQUIRED_BORROWER_MAILING_ADDRESS_FIELDS_FOREIGN
    : REQUIRED_BORROWER_MAILING_ADDRESS_FIELDS_US;

  const parsed = getParsedFields(data, requiredFields);

  if (!parsed) {
    return;
  }

  const hasMailingAddress = requiredFields.some((key) => parsed[key] != null);

  if (!hasMailingAddress) {
    return;
  }

  const missingFields: (keyof CreateLoanRow)[] = [];

  for (const key of requiredFields) {
    // @ts-ignore
    if (parsed[key] == null) {
      missingFields.push(key);
    }
  }

  if (missingFields.length) {
    const missingFieldsString = missingFields.map((key) => `\`${String(key)}\``).join(', ');
    ctx.addIssue({
      code: 'custom',
      message: `Borrower mailing address is missing required ${
        missingFields.length > 1 ? 'fields' : 'field'
      } ${missingFieldsString}.`,
    });
  }

  const result = StrictAddress.safeParse({
    line1: data.primaryBorrowerMailingAddressLine1,
    line2: data.primaryBorrowerMailingAddressLine2 ?? '',
    line3: data.primaryBorrowerMailingAddressLine3 ?? '',
    line4: data.primaryBorrowerMailingAddressLine4 ?? '',
    locality: data.primaryBorrowerMailingAddressLocality ?? '',
    region: data.primaryBorrowerMailingAddressRegion ?? '',
    postcode: data.primaryBorrowerMailingAddressPostcode ?? '',
    country: data.primaryBorrowerMailingAddressCountry ?? 'US',
  });

  if (!result.success) {
    const errorTree = z.treeifyError(result.error);
    for (const [key, issue] of Object.entries(errorTree.properties ?? {})) {
      ctx.addIssue({
        code: 'custom',
        message: issue.errors.join(', '),
        path: [`primaryBorrowerMailingAddress${capitalize(key)}`], // This maps keys to the correct field key
      });
    }
  }
};

const REQUIRED_ENTITY_FIELDS: (keyof CreateLoanRow)[] = ['entityName', 'entityEin'];

const hasEntityFields = (data: CreateLoanRow, ctx: z.RefinementCtx<CreateLoanRow>) => {
  const parsed = getParsedFields(data, [
    'entityCompanyId',
    'entityName',
    'entityEin',
    'entityAddressLine1',
    'entityAddressLocality',
    'entityAddressRegion',
    'entityAddressPostcode',
  ]);

  if (!parsed) {
    return;
  }

  const hasEntity = parsed.entityCompanyId || parsed.entityName;

  if (!hasEntity) {
    return;
  }

  const missingFields: (keyof CreateLoanRow)[] = [];

  for (const key of REQUIRED_ENTITY_FIELDS) {
    // @ts-ignore
    if (parsed[key] == null) {
      missingFields.push(key);
    }
  }

  if (missingFields.length) {
    const missingFieldsString = missingFields.map((key) => `\`${String(key)}\``).join(', ');
    ctx.addIssue({
      code: 'custom',
      message: `Entity missing required ${
        missingFields.length > 1 ? 'fields' : 'field'
      } ${missingFieldsString}. (Entity is denoted by presence of \`entityCompanyId\` or \`entityName\`)`,
    });
  }
};

const validateOutstandingAmount = (data: CreateLoanRow, ctx: z.RefinementCtx<CreateLoanRow>) => {
  const parsed = getParsedFields(data, ['currentOutstandingAmount', 'loanPrincipal', 'maxLineOfCredit']);

  if (!parsed) {
    return;
  }

  if (parsed.maxLineOfCredit != null && parsed.currentOutstandingAmount > parsed.maxLineOfCredit) {
    ctx.addIssue({
      code: 'custom',
      message: `currentOutstandingAmount (${parsed.currentOutstandingAmount}) cannot be greater than maxLineOfCredit (${parsed.maxLineOfCredit})`,
      path: ['currentOutstandingAmount'],
    });
  } else if (parsed.maxLineOfCredit == null && parsed.currentOutstandingAmount > parsed.loanPrincipal) {
    ctx.addIssue({
      code: 'custom',
      message: `currentOutstandingAmount (${parsed.currentOutstandingAmount}) cannot be greater than loanPrincipal (${parsed.loanPrincipal})`,
      path: ['currentOutstandingAmount'],
    });
  }
};

const checkPaymentTotal = (data: CreateLoanRow, ctx: z.RefinementCtx<CreateLoanRow>) => {
  const parsed = getParsedFields(data, [
    'firstPaymentTotal',
    'firstPaymentPrincipal',
    'firstPaymentPrincipalAndInterest',
    'firstPaymentInterest',
    'monthlyPaymentEscrow',
  ]);

  if (!parsed) {
    return;
  }

  // Default to zero. (other parsers validate presence)
  const {
    firstPaymentTotal = 0,
    firstPaymentPrincipal = 0,
    firstPaymentPrincipalAndInterest = 0,
    firstPaymentInterest = 0,
    monthlyPaymentEscrow = 0,
  } = parsed;

  // only check total if it exists
  if (!firstPaymentTotal) {
    return;
  }

  const principalAndInterest =
    firstPaymentPrincipalAndInterest > 0
      ? firstPaymentPrincipalAndInterest
      : add(firstPaymentPrincipal, firstPaymentInterest);

  if (add(principalAndInterest, monthlyPaymentEscrow) !== firstPaymentTotal) {
    ctx.addIssue({
      code: 'custom',
      message: 'Amounts provided for principal, interest, and escrow do not equal the total payment amount',
      path: ['firstPaymentTotal'],
    });
  }
};

const checkRemittanceFields = (data: CreateLoanRow, ctx: z.RefinementCtx<CreateLoanRow>) => {
  const parsed = getParsedFields(data, [
    'investorRemittanceRate',
    'servicerRemittanceRate',
    'investor2RemittanceRate',
    'guaranteeFeeRate',
    'loanInterestRateDecimal',
    'loanInterestRatePercent',
    'investorOwnershipPercent',
    'investor2OwnershipPercent',
  ]);

  if (!parsed) {
    return;
  }

  const {
    investorRemittanceRate,
    servicerRemittanceRate,
    investor2RemittanceRate,
    guaranteeFeeRate,
    loanInterestRateDecimal,
    loanInterestRatePercent,
    investorOwnershipPercent,
    investor2OwnershipPercent,
  } = parsed;

  const shouldCheckRemittanceFields = investorRemittanceRate || servicerRemittanceRate;
  if (!shouldCheckRemittanceFields) {
    return;
  }

  // Ensure both fields are present so we can sum them
  if (investorRemittanceRate == null || servicerRemittanceRate == null) {
    ctx.addIssue({
      code: 'custom',
      message: 'Both `investorRemittanceRate` and `servicerRemittanceRate` must be present',
    });
    return;
  }

  const interestRatePercent = loanInterestRatePercent
    ? Number(loanInterestRatePercent)
    : round(Number(loanInterestRateDecimal ?? 0) * 100, 5);

  // Ensure rates add up to total interest rate
  if (
    addPrecision(
      5,
      investorRemittanceRate,
      servicerRemittanceRate,
      investor2RemittanceRate ?? 0,
      guaranteeFeeRate ?? 0,
    ) !== interestRatePercent
  ) {
    ctx.addIssue({
      code: 'custom',
      message: 'Amounts provided for the investor remittance do not equal the interest rate',
    });
  }

  if (
    investorOwnershipPercent &&
    investor2OwnershipPercent &&
    add(investorOwnershipPercent, investor2OwnershipPercent) !== 100
  ) {
    ctx.addIssue({
      code: 'custom',
      message: 'Amounts provided for the ownership percentages do not add up to 100%',
    });
  }
};

const hasCoBorrowerNameFields = (data: CreateLoanRow, ctx: z.RefinementCtx<CreateLoanRow>) => {
  const CO_BORROWER_FIELDS_FOR_VALIDATION = range(1, 11)
    .map(
      (n) =>
        [
          `coBorrower${n}FirstName`,
          `coBorrower${n}LastName`,
          `coBorrower${n}Email`,
          `coBorrower${n}Phone`,
        ] as (keyof CreateLoanRow)[],
    )
    .flat();
  const parsed = getParsedFields(data, CO_BORROWER_FIELDS_FOR_VALIDATION);

  if (!parsed) {
    return;
  }

  const missingFields: string[] = [];

  for (const i of range(1, 11)) {
    // @ts-ignore
    if (parsed[`coBorrower${i}Email`] || parsed[`coBorrower${i}Phone`]) {
      for (const key of [`coBorrower${i}FirstName`, `coBorrower${i}LastName`]) {
        // @ts-ignore
        if (parsed[key] == null) {
          missingFields.push(key);
        }
      }
    }
  }

  if (missingFields.length) {
    const missingFieldsString = missingFields.map((key) => `\`${String(key)}\``).join(', ');
    ctx.addIssue({
      code: 'custom',
      message: `Missing required ${
        missingFields.length > 1 ? 'fields' : 'field'
      } for co-borrowers: ${missingFieldsString}`,
    });
  }
};

export const BASE_ESCROW_COUNTY_FIELDS_FOR_VALIDATION = [
  'taxCountyIsEscrowed',
  'taxCountyNextPaymentAmount',
  'taxCountyPayeeName',
  'addressCounty',
] as (keyof CreateLoanRow)[];
export const MULTI_PROP_ESCROW_COUNTY_FIELDS_FOR_VALIDATION = range(2, NUM_BOARDING_PROPERTIES + 1)
  .map(
    (n) =>
      [
        `address${n}TaxCountyIsEscrowed`,
        `address${n}TaxCountyNextPaymentAmount`,
        `address${n}TaxCountyPayeeName`,
        `address${n}County`,
      ] as (keyof CreateLoanRow)[],
  )
  .flat()
  .concat(BASE_ESCROW_COUNTY_FIELDS_FOR_VALIDATION);

const hasEscrowCountyFields =
  (context: CrossFieldParserContext) => (data: CreateLoanRow, ctx: z.RefinementCtx<CreateLoanRow>) => {
    const parsed =
      context === 'api'
        ? getParsedFields(data, MULTI_PROP_ESCROW_COUNTY_FIELDS_FOR_VALIDATION)
        : getParsedFields(data, BASE_ESCROW_COUNTY_FIELDS_FOR_VALIDATION);

    if (!parsed) {
      return;
    }

    const missingFields: string[] = [];

    if (
      parsed.taxCountyIsEscrowed != null ||
      (parsed.taxCountyNextPaymentAmount != null && parsed.taxCountyNextPaymentAmount > 0) ||
      parsed.taxCountyPayeeName != null
    ) {
      for (const key of [`taxCountyIsEscrowed`, `taxCountyNextPaymentAmount`]) {
        // @ts-ignore
        if (parsed[key] == null) {
          missingFields.push(key);
        }
      }

      if (!parsed.addressCounty) {
        ctx.addIssue({
          code: 'custom',
          message: 'Cannot add county taxes without required field: `addressCounty`',
        });
      }
    }

    if (context === 'api') {
      for (const i of range(2, NUM_BOARDING_PROPERTIES + 1)) {
        if (
          // @ts-ignore
          parsed[`address${i}TaxCountyIsEscrowed`] != null ||
          // @ts-ignore
          (parsed[`address${i}TaxCountyNextPaymentAmount`] != null &&
            parsed[`address${i}TaxCountyNextPaymentAmount`] > 0) ||
          // @ts-ignore
          parsed[`address${i}TaxCountyPayeeName`] != null
        ) {
          for (const key of [`address${i}TaxCountyIsEscrowed`, `address${i}TaxCountyNextPaymentAmount`]) {
            // @ts-ignore
            if (parsed[key] == null) {
              missingFields.push(key);
            }
          }

          if (!parsed[`address${i}County`]) {
            ctx.addIssue({
              code: 'custom',
              message: `Cannot add county taxes for address ${i} without required field: \`address${i}County\``,
            });
          }
        }
      }
    }

    if (missingFields.length) {
      const missingFieldsString = missingFields.map((key) => `\`${String(key)}\``).join(', ');
      ctx.addIssue({
        code: 'custom',
        message: `Missing required ${
          missingFields.length > 1 ? 'fields' : 'field'
        } for escrow county taxes: ${missingFieldsString}`,
      });
    }
  };

export const BASE_ESCROW_CITY_FIELDS_FOR_VALIDATION = [
  'taxCityIsEscrowed',
  'taxCityNextPaymentAmount',
  'taxCityPayeeName',
  'taxCityNextDueDate',
  'taxCityPaymentCadence',
] as (keyof CreateLoanRow)[];
export const MULTI_PROP_ESCROW_CITY_FIELDS_FOR_VALIDATION = range(2, NUM_BOARDING_PROPERTIES + 1)
  .map(
    (n) =>
      [
        `address${n}TaxCityIsEscrowed`,
        `address${n}TaxCityNextPaymentAmount`,
        `address${n}TaxCityPayeeName`,
        `address${n}TaxCityNextDueDate`,
        `address${n}TaxCityPaymentCadence`,
      ] as (keyof CreateLoanRow)[],
  )
  .flat()
  .concat(BASE_ESCROW_CITY_FIELDS_FOR_VALIDATION);

const hasEscrowCityFields =
  (context: CrossFieldParserContext) => (data: CreateLoanRow, ctx: z.RefinementCtx<CreateLoanRow>) => {
    const parsed =
      context === 'api'
        ? getParsedFields(data, MULTI_PROP_ESCROW_CITY_FIELDS_FOR_VALIDATION)
        : getParsedFields(data, BASE_ESCROW_CITY_FIELDS_FOR_VALIDATION);

    if (!parsed) {
      return;
    }

    const missingFields: string[] = [];

    if (
      parsed.taxCityIsEscrowed != null ||
      (parsed.taxCityNextPaymentAmount != null && parsed.taxCityNextPaymentAmount > 0) ||
      parsed.taxCityPayeeName != null
    ) {
      for (const key of [
        `taxCityIsEscrowed`,
        `taxCityNextPaymentAmount`,
        `taxCityPayeeName`,
        `taxCityNextDueDate`,
        `taxCityPaymentCadence`,
      ]) {
        // @ts-ignore
        if (parsed[key] == null) {
          missingFields.push(key);
        }
      }
    }

    if (context === 'api') {
      for (const i of range(2, NUM_BOARDING_PROPERTIES + 1)) {
        if (
          // @ts-ignore
          parsed[`address${i}TaxCityIsEscrowed`] != null ||
          // @ts-ignore
          (parsed[`address${i}TaxCityNextPaymentAmount`] != null &&
            parsed[`address${i}TaxCityNextPaymentAmount`] > 0) ||
          // @ts-ignore
          parsed[`address${i}TaxCityPayeeName`] != null
        ) {
          for (const key of [
            `address${i}TaxCityIsEscrowed`,
            `address${i}TaxCityNextPaymentAmount`,
            `address${i}TaxCityPayeeName`,
          ]) {
            // @ts-ignore
            if (parsed[key] == null) {
              missingFields.push(key);
            }
          }
        }
      }
    }

    if (missingFields.length) {
      const missingFieldsString = missingFields.map((key) => `\`${String(key)}\``).join(', ');
      ctx.addIssue({
        code: 'custom',
        message: `Missing required ${
          missingFields.length > 1 ? 'fields' : 'field'
        } for escrow city taxes: ${missingFieldsString}`,
      });
    }
  };

export const BASE_ESCROW_SCHOOL_FIELDS_FOR_VALIDATION = [
  'taxSchoolIsEscrowed',
  'taxSchoolNextPaymentAmount',
  'taxSchoolPayeeName',
  'taxSchoolNextDueDate',
  'taxSchoolPaymentCadence',
] as (keyof CreateLoanRow)[];
export const MULTI_PROP_ESCROW_SCHOOL_FIELDS_FOR_VALIDATION = range(2, NUM_BOARDING_PROPERTIES + 1)
  .map(
    (n) =>
      [
        `address${n}TaxSchoolIsEscrowed`,
        `address${n}TaxSchoolNextPaymentAmount`,
        `address${n}TaxSchoolPayeeName`,
        `address${n}TaxSchoolNextDueDate`,
        `address${n}TaxSchoolPaymentCadence`,
      ] as (keyof CreateLoanRow)[],
  )
  .flat()
  .concat(BASE_ESCROW_SCHOOL_FIELDS_FOR_VALIDATION);

const hasEscrowSchoolFields =
  (context: CrossFieldParserContext) => (data: CreateLoanRow, ctx: z.RefinementCtx<CreateLoanRow>) => {
    const parsed =
      context === 'api'
        ? getParsedFields(data, MULTI_PROP_ESCROW_SCHOOL_FIELDS_FOR_VALIDATION)
        : getParsedFields(data, BASE_ESCROW_SCHOOL_FIELDS_FOR_VALIDATION);

    if (!parsed) {
      return;
    }

    const missingFields: string[] = [];

    if (
      parsed.taxSchoolIsEscrowed != null ||
      (parsed.taxSchoolNextPaymentAmount != null && parsed.taxSchoolNextPaymentAmount > 0) ||
      parsed.taxSchoolPayeeName != null
    ) {
      for (const key of [
        'taxSchoolIsEscrowed',
        'taxSchoolNextPaymentAmount',
        'taxSchoolPayeeName',
        'taxSchoolNextDueDate',
        'taxSchoolPaymentCadence',
      ]) {
        // @ts-ignore
        if (parsed[key] == null) {
          missingFields.push(key);
        }
      }
    }

    if (context === 'api') {
      for (const i of range(2, NUM_BOARDING_PROPERTIES + 1)) {
        if (
          // @ts-ignore
          parsed[`address${i}TaxSchoolIsEscrowed`] != null ||
          // @ts-ignore
          (parsed[`address${i}TaxSchoolNextPaymentAmount`] != null &&
            parsed[`address${i}TaxSchoolNextPaymentAmount`] > 0) ||
          // @ts-ignore
          parsed[`address${i}TaxSchoolPayeeName`] != null
        ) {
          for (const key of [
            `address${i}TaxSchoolIsEscrowed`,
            `address${i}TaxSchoolNextPaymentAmount`,
            `address${i}TaxSchoolPayeeName`,
            `address${i}TaxSchoolNextDueDate`,
            `address${i}TaxSchoolPaymentCadence`,
          ]) {
            // @ts-ignore
            if (parsed[key] == null) {
              missingFields.push(key);
            }
          }
        }
      }
    }

    if (missingFields.length) {
      const missingFieldsString = missingFields.map((key) => `\`${String(key)}\``).join(', ');
      ctx.addIssue({
        code: 'custom',
        message: `Missing required ${
          missingFields.length > 1 ? 'fields' : 'field'
        } for escrow school taxes: ${missingFieldsString}`,
      });
    }
  };

export const BASE_ESCROW_OTHER_FIELDS_FOR_VALIDATION = range(1, 4)
  .map((n) => [
    `taxOther${n}IsEscrowed`,
    `taxOther${n}NextPaymentAmount`,
    `taxOther${n}PayeeName`,
    `taxOther${n}NextDueDate`,
    `taxOther${n}PaymentCadence`,
  ])
  .flat(1) as (keyof CreateLoanRow)[];
export const MULTI_PROP_ESCROW_OTHER_FIELDS_FOR_VALIDATION = (
  flattenDeep(
    range(2, NUM_BOARDING_PROPERTIES + 1).map((p) =>
      range(1, NUM_BOARDING_TAX_OTHERS + 1).map((n) => [
        `address${p}TaxOther${n}IsEscrowed`,
        `address${p}TaxOther${n}NextPaymentAmount`,
        `address${p}TaxOther${n}PayeeName`,
        `address${p}TaxOther${n}NextDueDate`,
        `address${p}TaxOther${n}PaymentCadence`,
      ]),
    ),
  ) as (keyof CreateLoanRow)[]
).concat(BASE_ESCROW_OTHER_FIELDS_FOR_VALIDATION);

const hasEscrowOtherTaxFields =
  (context: CrossFieldParserContext) => (data: CreateLoanRow, ctx: z.RefinementCtx<CreateLoanRow>) => {
    const parsed =
      context === 'api'
        ? getParsedFields(data, MULTI_PROP_ESCROW_OTHER_FIELDS_FOR_VALIDATION)
        : getParsedFields(data, BASE_ESCROW_OTHER_FIELDS_FOR_VALIDATION);

    if (!parsed) {
      return;
    }

    const missingFields: string[] = [];

    for (const n of range(1, 4)) {
      if (
        // @ts-ignore
        parsed[`taxOther${n}IsEscrowed`] != null ||
        // @ts-ignore
        (parsed[`taxOther${n}NextPaymentAmount`] != null && parsed[`taxOther${n}NextPaymentAmount`] > 0) ||
        // @ts-ignore
        parsed[`taxOther${n}PayeeName`] != null
      ) {
        for (const key of [
          `taxOther${n}IsEscrowed`,
          `taxOther${n}NextPaymentAmount`,
          `taxOther${n}PayeeName`,
          `taxOther${n}NextDueDate`,
          `taxOther${n}PaymentCadence`,
        ]) {
          // @ts-ignore
          if (parsed[key] == null) {
            missingFields.push(key);
          }
        }
      }
    }

    if (context === 'api') {
      for (const p of range(2, NUM_BOARDING_PROPERTIES + 1)) {
        for (const n of range(1, 4)) {
          if (
            // @ts-ignore
            parsed[`address${p}TaxOther${n}IsEscrowed`] != null ||
            // @ts-ignore
            (parsed[`address${p}TaxOther${n}NextPaymentAmount`] != null &&
              parsed[`address${p}TaxOther${n}NextPaymentAmount`] > 0) ||
            // @ts-ignore
            parsed[`address${p}TaxOther${n}PayeeName`] != null
          ) {
            for (const key of [
              `address${p}TaxOther${n}IsEscrowed`,
              `address${p}TaxOther${n}NextPaymentAmount`,
              `address${p}TaxOther${n}PayeeName`,
              `address${p}TaxOther${n}NextDueDate`,
              `address${p}TaxOther${n}PaymentCadence`,
            ]) {
              // @ts-ignore
              if (parsed[key] == null) {
                missingFields.push(key);
              }
            }
          }
        }
      }
    }

    if (missingFields.length) {
      const missingFieldsString = missingFields.map((key) => `\`${String(key)}\``).join(', ');
      ctx.addIssue({
        code: 'custom',
        message: `Missing required ${
          missingFields.length > 1 ? 'fields' : 'field'
        } for escrow other taxes: ${missingFieldsString}`,
      });
    }
  };

export const BASE_INSURANCE_FIELDS_FOR_VALIDATION = range(1, NUM_BOARDING_INSURANCES + 1)
  .map((n) => [
    `insurance${n}IsEscrowed`,
    `insurance${n}Amount`,
    `insurance${n}CoverageAmount`,
    `insurance${n}CompanyName`,
    `insurance${n}PolicyNumber`,
    `insurance${n}PolicyStart`,
    `insurance${n}PolicyEnd`,
  ])
  .flat(1) as (keyof CreateLoanRow)[];
export const MULTI_PROP_INSURANCE_FIELDS_FOR_VALIDATION = (
  flattenDeep(
    range(2, NUM_BOARDING_PROPERTIES + 1).map((p) =>
      range(1, NUM_BOARDING_INSURANCES + 1).map((n) => [
        `address${p}Insurance${n}IsEscrowed`,
        `address${p}Insurance${n}Amount`,
        `address${p}Insurance${n}CoverageAmount`,
        `address${p}Insurance${n}CompanyName`,
        `address${p}Insurance${n}PolicyNumber`,
        `address${p}Insurance${n}PolicyStart`,
        `address${p}Insurance${n}PolicyEnd`,
      ]),
    ),
  ) as (keyof CreateLoanRow)[]
).concat(BASE_INSURANCE_FIELDS_FOR_VALIDATION);

const hasInsuranceEscrowFields =
  (context: CrossFieldParserContext) => (data: CreateLoanRow, ctx: z.RefinementCtx<CreateLoanRow>) => {
    const parsed =
      context === 'api'
        ? getParsedFields(data, MULTI_PROP_INSURANCE_FIELDS_FOR_VALIDATION)
        : getParsedFields(data, BASE_INSURANCE_FIELDS_FOR_VALIDATION);

    const missingFields: string[] = [];

    for (const n of range(1, NUM_BOARDING_INSURANCES + 1)) {
      if (
        // @ts-ignore
        parsed[`insurance${n}IsEscrowed`] != null ||
        // @ts-ignore
        (parsed[`insurance${n}Amount`] != null && parsed[`insurance${n}Amount`] > 0) ||
        // @ts-ignore
        parsed[`insurance${n}CompanyName`] != null
      ) {
        for (const key of [
          `insurance${n}IsEscrowed`,
          `insurance${n}Amount`,
          `insurance${n}CompanyName`,
          `insurance${n}PolicyEnd`,
        ]) {
          // @ts-ignore
          if (parsed[key] == null) {
            missingFields.push(key);
          }
        }
      }
    }

    if (context === 'api') {
      for (const p of range(2, NUM_BOARDING_PROPERTIES + 1)) {
        for (const n of range(1, NUM_BOARDING_INSURANCES + 1)) {
          if (
            // @ts-ignore
            parsed[`address${p}Insurance${n}IsEscrowed`] != null ||
            // @ts-ignore
            (parsed[`address${p}Insurance${n}Amount`] != null && parsed[`address${p}Insurance${n}Amount`] > 0) ||
            // @ts-ignore
            parsed[`address${p}Insurance${n}CompanyName`] != null ||
            // @ts-ignore
            parsed[`address${p}Insurance${n}PolicyNumber`] != null
          ) {
            for (const key of [
              `address${p}Insurance${n}IsEscrowed`,
              `address${p}Insurance${n}Amount`,
              `address${p}Insurance${n}CompanyName`,
              `address${p}Insurance${n}PolicyNumber`,
              `address${p}Insurance${n}PolicyEnd`,
            ]) {
              // @ts-ignore
              if (parsed[key] == null) {
                missingFields.push(key);
              }
            }
          }
        }
      }
    }

    if (missingFields.length) {
      const missingFieldsString = missingFields.map((key) => `\`${String(key)}\``).join(', ');
      ctx.addIssue({
        code: 'custom',
        message: `Missing required ${
          missingFields.length > 1 ? 'fields' : 'field'
        } for insurance escrow item: ${missingFieldsString}`,
      });
    }
  };

const BASE_MORTGAGE_INSURANCE_FIELDS_FOR_VALIDATION = [
  'miIsEscrowed',
  'miAmount',
  'miCompanyName',
  'miStartDate',
  'miPaymentCadence',
  'miCertificateId',
] as (keyof CreateLoanRow)[];

const hasMortgageInsuranceFields =
  (_: CrossFieldParserContext) => (data: CreateLoanRow, ctx: z.RefinementCtx<CreateLoanRow>) => {
    const parsed = getParsedFields(data, BASE_MORTGAGE_INSURANCE_FIELDS_FOR_VALIDATION);

    const missingFields: string[] = [];

    if (parsed.miIsEscrowed != null || parsed.miAmount != null) {
      for (const key of [
        'miAmount',
        'miIsEscrowed',
        'miCompanyName',
        'miStartDate',
        'miPaymentCadence',
        'miCertificateId',
      ]) {
        if (parsed[key] == null) {
          missingFields.push(key);
        }
      }
    }

    if (missingFields.length) {
      const missingFieldsString = missingFields.map((key) => `\`${String(key)}\``).join(', ');
      ctx.addIssue({
        code: 'custom',
        message: `Missing required ${
          missingFields.length > 1 ? 'fields' : 'field'
        } for mortgage insurance escrow item: ${missingFieldsString}`,
      });
    }
  };

const checkReserveFields = (data: CreateLoanRow, ctx: z.RefinementCtx<CreateLoanRow>) => {
  const parsed = getParsedFields(data, [
    'isInterestReserve',
    'buydownType',
    'initialReserveBalance',
    'isPendingActivation',
  ]);

  if (!parsed) {
    return;
  }

  if (parsed.isInterestReserve && !parsed.initialReserveBalance && !parsed.isPendingActivation) {
    ctx.addIssue({
      code: 'custom',
      message: 'Loan with `isInterestReserve` = true must have `initialReserveBalance` greater than 0.',
      path: ['isInterestReserve'],
    });
  }

  if (parsed.buydownType && !parsed.initialReserveBalance && !parsed.isPendingActivation) {
    ctx.addIssue({
      code: 'custom',
      message: 'Loan with `buydownType` must have `initialReserveBalance` greater than 0.',
      path: ['buydownType'],
    });
  }

  if (parsed.initialReserveBalance && !parsed.isInterestReserve && !parsed.buydownType) {
    ctx.addIssue({
      code: 'custom',
      message: 'Reserve balance will be ignored unless `isInterestReserve` = true or `buydownType` is defined',
      path: ['initialReserveBalance'],
    });
  }
};

const checkDefaultInterestFields = (data: CreateLoanRow, ctx: z.RefinementCtx<CreateLoanRow>) => {
  const parsed = getParsedFields(data, [
    'defaultInterestRateDecimal',
    'defaultInterestRatePercent',
    'defaultInterestEffectiveDate',
    'firstPaymentDate',
    'paymentFrequency',
  ]);

  if (!parsed) {
    return;
  }

  if (parsed.defaultInterestRateDecimal != null && parsed.defaultInterestRatePercent != null) {
    ctx.addIssue({
      code: 'custom',
      message: 'defaultInterestRateDecimal and defaultInterestRatePercent cannot be set at the same time.',
      path: ['defaultInterestRateDecimal'],
    });
  }

  if (
    parsed.paymentFrequency === 1 &&
    (parsed.defaultInterestRateDecimal != null || parsed.defaultInterestRatePercent != null)
  ) {
    ctx.addIssue({
      code: 'custom',
      message: 'Default interest rate decimal or percent cannot be set for annual payment frequency.',
      path: ['paymentFrequency'],
    });
  }

  if (parsed.defaultInterestEffectiveDate) {
    if (!parsed.defaultInterestRateDecimal && !parsed.defaultInterestRatePercent) {
      ctx.addIssue({
        code: 'custom',
        message: 'defaultInterestEffectiveDate cannot be set without a default interest rate.',
        path: ['defaultInterestEffectiveDate'],
      });
    }

    if (new Date(parsed.defaultInterestEffectiveDate) < new Date(parsed.firstPaymentDate)) {
      ctx.addIssue({
        code: 'custom',
        message: 'defaultInterestEffectiveDate cannot be before firstPaymentDate.',
        path: ['defaultInterestEffectiveDate'],
      });
    }

    if (new Date(parsed.defaultInterestEffectiveDate) > new Date(parsed.maturityDate)) {
      ctx.addIssue({
        code: 'custom',
        message: 'defaultInterestEffectiveDate cannot be after the maturityDate.',
        path: ['defaultInterestEffectiveDate'],
      });
    }

    const maxDefaultInterestEffectiveDate = DateTime.now().set({ day: 1 }).plus({ months: 1 }).toJSDate();

    if (new Date(parsed.defaultInterestEffectiveDate) > maxDefaultInterestEffectiveDate) {
      ctx.addIssue({
        code: 'custom',
        message: `defaultInterestEffectiveDate cannot be after ${formatDate(
          DateTime.fromJSDate(maxDefaultInterestEffectiveDate),
        )}`,
        path: ['defaultInterestEffectiveDate'],
      });
    }
  }
};

const deprecateInterestReserveField = (data: CreateLoanRow, ctx: z.RefinementCtx<CreateLoanRow>) => {
  if (data.initialInterestReserveBalance) {
    ctx.addIssue({
      code: 'custom',
      message: 'Field is deprecated. Use `initialReserveBalance` instead.',
      path: ['initialInterestReserveBalance'],
    });
  }
};

const hasLoanTypeOrAmortizationTypeField = (data: CreateLoanRow, ctx: z.RefinementCtx<CreateLoanRow>) => {
  const parsed = getParsedFields(data, ['loanType', 'amortizationType']);

  if (!parsed) {
    return;
  }

  if (!parsed.loanType && !parsed.amortizationType) {
    ctx.addIssue({
      code: 'custom',
      message: 'Missing required field `amortizationType`.',
    });
  }
};

const hasBoardedDateOfFirstDelinquency =
  (options: { investorReportingEnabled: boolean; wipCreditReportingEnabled: boolean }) =>
  (data: CreateLoanRow, ctx: z.RefinementCtx<CreateLoanRow>) => {
    if (!options.investorReportingEnabled && !options.wipCreditReportingEnabled) {
      return;
    }

    const parsed = getParsedFields(data, ['firstPaymentDate', 'firstCollectedPaymentDate', 'dateOfFirstDelinquency']);

    if (!parsed || !parsed.firstCollectedPaymentDate || !parsed.firstPaymentDate) {
      return;
    }

    const firstCollectedPaymentDate = DateTime.fromJSDate(new Date(parsed.firstCollectedPaymentDate)).startOf('day');
    const firstPaymentDate = DateTime.fromJSDate(new Date(parsed.firstPaymentDate)).startOf('day');

    // Only required when Willow is taking over a loan that's already mid-life — i.e. the prior
    // servicer collected at least one payment (firstCollectedPaymentDate > firstPaymentDate)
    // AND that boarding happens in the past (a future-dated firstCollectedPaymentDate means
    // Willow is preparing to start collecting on a date that hasn't arrived yet).
    const firstCollectedAfterFirstPayment = firstCollectedPaymentDate > firstPaymentDate;
    const firstCollectedIsInPast = firstCollectedPaymentDate < DateTime.now().startOf('day');

    if (firstCollectedAfterFirstPayment && firstCollectedIsInPast && !parsed.dateOfFirstDelinquency) {
      ctx.addIssue({
        code: 'custom',
        message: 'Missing required field `dateOfFirstDelinquency` when boarding a loan that is 30+ days past due',
        path: ['dateOfFirstDelinquency'],
      });
    }
  };

const hasAcquiredLoanFields = (data: CreateLoanRow, ctx: z.RefinementCtx<CreateLoanRow>) => {
  const parsed = getParsedFields(data, ['isAcquired', 'acquisitionDate', 'firstCollectedPaymentDate']);

  if (!parsed) {
    return;
  }

  if (parsed.isAcquired && !parsed.acquisitionDate) {
    ctx.addIssue({
      code: 'custom',
      message: 'Acquired loan must have `acquisitionDate`.',
      path: ['isAcquired'],
    });
  }

  if (parsed.acquisitionDate) {
    if (!parsed.firstCollectedPaymentDate) {
      ctx.addIssue({
        code: 'custom',
        message: 'Loan with `acquisitionDate` must have `firstCollectedPaymentDate`.',
        path: ['acquisitionDate'],
      });
    }

    if (
      parsed.firstCollectedPaymentDate &&
      convertStringToDate(parsed.acquisitionDate) > convertStringToDate(parsed.firstCollectedPaymentDate)
    ) {
      ctx.addIssue({
        code: 'custom',
        message: '`acquisitionDate` must be before `firstCollectedPaymentDate`.',
        path: ['acquisitionDate'],
      });
    }
  }
};

const validateFeeFields = (data: CreateLoanRow, ctx: z.RefinementCtx<CreateLoanRow>) => {
  const parsed = getParsedFields(data, ['lateFeeMin', 'lateFeeMax']);

  if (!parsed) {
    return;
  }

  if (parsed.lateFeeMin && parsed.lateFeeMax && parsed.lateFeeMin > parsed.lateFeeMax) {
    ctx.addIssue({
      code: 'custom',
      message: 'lateFeeMin must be less than lateFeeMax',
      path: ['lateFeeMin'],
    });
  }
};

const validateHelocLoanFields = (data: CreateLoanRow, ctx: z.RefinementCtx<CreateLoanRow>) => {
  const parsed = getParsedFields(data, [
    'productType',
    'interestOnlyFlag',
    'interestOnlyTermMonths',
    'interestOnlyTermYears',
    'interestOnlyEndDate',
  ]);

  if (parsed.productType !== 'heloc') {
    return;
  }

  if (!parsed.interestOnlyFlag) {
    ctx.addIssue({
      code: 'custom',
      message: 'Heloc loan requires interest only flag be set to true',
      path: ['productType'],
    });
  }

  if (!parsed.interestOnlyTermMonths && !parsed.interestOnlyTermYears && !parsed.interestOnlyEndDate) {
    ctx.addIssue({
      code: 'custom',
      message:
        'Heloc loan requires the interest only term set via interestOnlyTermMonths, interestOnlyTermYears, or interestOnlyEndDate',
      path: ['productType'],
    });
  }
};

export const validateBillingDates =
  (allowPaymentDueDateAnyDayOfMonth: boolean) => (data: CreateLoanRow, ctx: z.RefinementCtx<CreateLoanRow>) => {
    const parsed = getParsedFields(data, [
      'firstPaymentDate',
      'loanMaturityDate',
      'firstCollectedPaymentDate',
      'productType',
      'constructionLoanType',
    ]);
    if (!parsed) {
      return;
    }

    const isHelocOrConstructionLoan =
      isHelocLoan(parsed.productType) || isConstructionLoan(parsed.constructionLoanType);

    if (isHelocOrConstructionLoan) {
      // assertValidFirstPaymentDate handles 1st/25th logic
      return;
    }

    const firstPaymentDate =
      parsed.firstPaymentDate != null ? DateTime.fromJSDate(convertStringToDate(parsed.firstPaymentDate)) : undefined;
    const loanMaturityDate =
      parsed.loanMaturityDate != null ? DateTime.fromJSDate(convertStringToDate(parsed.loanMaturityDate)) : undefined;
    const firstCollectedPaymentDate =
      parsed.firstCollectedPaymentDate != null
        ? DateTime.fromJSDate(convertStringToDate(parsed.firstCollectedPaymentDate))
        : undefined;

    if (!allowPaymentDueDateAnyDayOfMonth) {
      if (firstPaymentDate && firstPaymentDate.day !== 1) {
        ctx.addIssue({
          code: 'custom',
          message: 'First payment date must be on the first of the month.',
          path: ['firstPaymentDate'],
        });
      }

      if (loanMaturityDate && loanMaturityDate.day !== 1) {
        ctx.addIssue({
          code: 'custom',
          message: 'Loan maturity date must be on the first of the month.',
          path: ['loanMaturityDate'],
        });
      }

      if (firstCollectedPaymentDate && firstCollectedPaymentDate.day !== 1) {
        ctx.addIssue({
          code: 'custom',
          message: 'First collected payment date must be on the first of the month.',
          path: ['firstCollectedPaymentDate'],
        });
      }
    }
  };

const validateCompletionCertFields = (data: CreateLoanRow, ctx: z.RefinementCtx<CreateLoanRow>) => {
  const parsed = getParsedFields(data, ['completionCertNotes', 'completionCertFlag']);

  const notesArePresent = parsed.completionCertNotes != null && parsed.completionCertNotes !== '';
  const flagIsMissing = parsed.completionCertFlag == null;

  if (notesArePresent && flagIsMissing) {
    ctx.addIssue({
      code: 'custom',
      message: 'completionCertFlag is required when completionCertNotes is present',
      path: ['completionCertNotes'],
    });
  }
};

export type CreateLoanCrossFieldParserOptions = {
  investorReportingEnabled?: boolean;
  wipCreditReportingEnabled?: boolean;
};

export const getCreateLoanCrossFieldParser = (
  context: CrossFieldParserContext,
  allowPaymentDueDateAnyDayOfMonth: boolean,
  options?: CreateLoanCrossFieldParserOptions,
) =>
  z
    .any()
    .superRefine(validateIdFields)
    .superRefine(dateFieldsAndLoanTermMatch)
    .superRefine(hasPaymentFields)
    .superRefine(checkFirstCollectedPaymentDate)
    .superRefine(hasDrawFields)
    .superRefine(validateInterestOnlyFields)
    .superRefine(hasEscrowFields)
    .superRefine(hasActiveLoanFields)
    .superRefine(hasBorrowerMailingAddressFields)
    .superRefine(hasEntityFields)
    .superRefine(hasCoBorrowerNameFields)
    .superRefine(checkPaymentTotal)
    .superRefine(validateOutstandingAmount)
    .superRefine(checkRemittanceFields)
    .superRefine(checkReserveFields)
    .superRefine(checkDefaultInterestFields)
    .superRefine(deprecateInterestReserveField)
    .superRefine(hasLoanTypeOrAmortizationTypeField)
    .superRefine(hasAcquiredLoanFields)
    .superRefine(hasEscrowCountyFields(context))
    .superRefine(hasEscrowCityFields(context))
    .superRefine(hasEscrowSchoolFields(context))
    .superRefine(hasEscrowOtherTaxFields(context))
    .superRefine(hasInsuranceEscrowFields(context))
    .superRefine(hasMortgageInsuranceFields(context))
    .superRefine(validateEmailAddresses)
    .superRefine(validateArmFields)
    .superRefine(validateInvestorFields({ investorReportingEnabled: options?.investorReportingEnabled ?? false }))
    .superRefine(
      hasBoardedDateOfFirstDelinquency({
        investorReportingEnabled: options?.investorReportingEnabled ?? false,
        wipCreditReportingEnabled: options?.wipCreditReportingEnabled ?? false,
      }),
    )
    .superRefine(validateFeeFields)
    .superRefine(validateTerminationFeeFields)
    .superRefine(validatePaymentFrequencyFields)
    .superRefine(validateAppraisalFields([validateOriginalAppraisalDateIsBeforeCorrespondingAppraisalDate]))
    .superRefine(validateHelocLoanFields)
    .superRefine(validateBillingDates(allowPaymentDueDateAnyDayOfMonth))
    .superRefine(validateCompletionCertFields);
